import { useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { useQuery } from "@apollo/client/react";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import FullScreenModal from "ds/components/FullScreenModal";
import useErrorHandle from "hooks/useErrorHandle";
import { TemplateDeployment } from "types/generated";
import { TEMPLATE_DEPLOYMENT_QUERY } from "views/Account/TemplateDeployment/gql";

import useTemplateVersion from "../useTemplateVersion";
import useUpdateDeploymentCompatibleVersions from "./useCompatibleVersions";
import UpdateDeploymentForm from "./UpdateDeploymentForm";

function isValidLocationState(state: unknown): state is { returnUrl?: string } {
  return (
    state !== null &&
    typeof state === "object" &&
    (!("returnUrl" in state) || typeof (state as { returnUrl?: unknown }).returnUrl === "string")
  );
}

const UpdateDeployment = () => {
  const { versionId, templateId, deploymentId } = useParams<{
    versionId: string;
    templateId: string;
    deploymentId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    error: versionError,
    loading: versionLoading,
    data: versionData,
  } = useTemplateVersion(templateId, versionId);
  const template = versionData?.template;

  const {
    error: compatibleVersionsError,
    loading: compatibleVersionsLoading,
    data: compatibleVersionsData,
  } = useUpdateDeploymentCompatibleVersions(templateId, versionId);

  // Get returnUrl from location state (passed from the table) for instant display
  const locationState = isValidLocationState(location.state) ? location.state : undefined;
  const returnUrl = locationState?.returnUrl;

  // Fetch deployment from API (handles page reloads and ensures fresh data)
  const {
    error: deploymentError,
    loading: deploymentLoading,
    data: deploymentData,
  } = useQuery(TEMPLATE_DEPLOYMENT_QUERY, {
    variables: {
      templateID: templateId!,
      id: deploymentId!,
    },
    skip: !templateId || !deploymentId,
  });

  // Use state deployment for instant display, fall back to fetched data
  const deployment = deploymentData?.templateDeployment;

  useErrorHandle(versionError);
  useErrorHandle(deploymentError);
  useErrorHandle(compatibleVersionsError);

  // Determine back link: use returnUrl from navigation state, or fall back to version-level deployments
  const backLinkUrl =
    returnUrl ||
    (versionId
      ? `/template/${templateId}/version/${versionId}/deployments`
      : `/template/${templateId}/deployments`);
  const backUrl = getBreadcrumbsBackUrl(backLinkUrl, "");

  const handleClose = useCallback(() => {
    navigate(backUrl);
  }, [navigate, backUrl]);

  const availableVersions = useMemo(() => {
    const currentVersion = template?.templateVersion;
    if (!currentVersion) return [];
    const versions = compatibleVersionsData?.template?.compatibleVersions ?? [];
    return [currentVersion, ...versions.filter((v) => v.id !== currentVersion.id)];
  }, [template?.templateVersion, compatibleVersionsData]);

  const loading =
    (versionLoading && !template?.templateVersion) ||
    (deploymentLoading && !deployment) ||
    (compatibleVersionsLoading && !compatibleVersionsData?.template);

  return (
    <FullScreenModal title="Update deployment" onClose={handleClose}>
      {loading && <PageLoading />}
      {!loading && (!template?.templateVersion || !templateId || !versionId || !deployment) && (
        <NotFoundPage />
      )}
      {!loading && template?.templateVersion && templateId && versionId && deployment && (
        <UpdateDeploymentForm
          version={template.templateVersion}
          availableVersions={availableVersions}
          deployment={deployment as TemplateDeployment}
          templateId={templateId}
          onClose={handleClose}
        />
      )}
    </FullScreenModal>
  );
};

export default UpdateDeployment;
